export const IG_RESEARCH_SETTINGS_PATH = (userId) =>
  `/World/${userId}/settings/instagramResearchSettings`;

export const IG_RESEARCH_DEFAULT_LANGUAGE = 'en';

export const IG_RESEARCH_LANGUAGE_OPTIONS = [
  { id: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { id: 'en', label: 'English', flag: '🇬🇧' },
];

export function normalizeIgResearchLanguage(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'de' || v === 'german' || v === 'deutsch') return 'de';
  if (v === 'en' || v === 'english') return 'en';
  return IG_RESEARCH_DEFAULT_LANGUAGE;
}

/** Hidden until the Team-Dashboard licence is on — never a greyed-out card. */
export function isResearchModuleVisible(mod, { research, researchLoading } = {}) {
  if (!mod?.hideIfUnlicensed) return true;
  if (researchLoading) return false;
  return research?.[mod.licenseKey] === true;
}

export function isIgResearchSettingsVisible(ctx) {
  return isResearchModuleVisible(
    { hideIfUnlicensed: true, licenseKey: 'hasInstagramResearch' },
    ctx,
  );
}

export function languageFromIgSettings(val) {
  return normalizeIgResearchLanguage(val && typeof val === 'object' ? val.language : val);
}

export function igResearchBlockLabels(language) {
  return normalizeIgResearchLanguage(language) === 'de'
    ? {
      keyFacts: 'Wichtige Fakten',
      salesAngle: 'Sales-Ansatz',
      audience: 'Zielgruppe',
      topics: 'Themen',
      usps: 'USPs',
      painPoints: 'Schmerzpunkte',
    }
    : {
      keyFacts: 'Key facts',
      salesAngle: 'Sales angle',
      audience: 'Audience',
      topics: 'Topics',
      usps: 'USPs',
      painPoints: 'Pain points',
    };
}

export function formatIgResearchMetricBits(metrics = {}, followerCount, language) {
  const de = normalizeIgResearchLanguage(language) === 'de';
  const bits = [];
  if (metrics.activityScore != null) {
    bits.push(`${de ? 'Aktivität' : 'Activity'} ${metrics.activityScore}/10`);
  }
  if (metrics.engagementScore != null) {
    bits.push(`Engagement ${metrics.engagementScore}/10`);
  }
  if (metrics.avgPostsPerWeek != null) {
    bits.push(`${metrics.avgPostsPerWeek} ${de ? 'Posts/W' : 'posts/w'}`);
  }
  if (followerCount != null) {
    const pretty = followerCount >= 1000
      ? `${(followerCount / 1000).toFixed(1).replace(/\.0$/, '')}K`
      : followerCount;
    bits.push(`${pretty} ${de ? 'Follower' : 'followers'}`);
  }
  if (metrics.engagementTrend && metrics.engagementTrend !== 'stable') {
    bits.push(`Trend: ${metrics.engagementTrend}`);
  }
  return bits;
}

export function buildIgResearchDescriptionBlock({
  ai,
  metrics,
  followerCount,
  language,
  extras = false,
} = {}) {
  const labels = igResearchBlockLabels(language);
  const lines = [];
  if (ai?.summary) lines.push(ai.summary);
  if (ai?.uniqueFacts?.length) lines.push(`${labels.keyFacts}: ${ai.uniqueFacts.join('; ')}`);
  if (ai?.salesAngle) lines.push(`${labels.salesAngle}: ${ai.salesAngle}`);
  if (extras && ai?.sellingPoints?.length) lines.push(`${labels.usps}: ${ai.sellingPoints.join('; ')}`);
  if (extras && ai?.painPoints?.length) lines.push(`${labels.painPoints}: ${ai.painPoints.join('; ')}`);
  const metricBits = formatIgResearchMetricBits(metrics, followerCount, language);
  if (metricBits.length) lines.push(metricBits.join(' · '));
  if (ai?.targetAudience) lines.push(`${labels.audience}: ${ai.targetAudience}`);
  if (ai?.topics?.length) lines.push(`${labels.topics}: ${ai.topics.join(', ')}`);
  return lines.join(' | ');
}
